import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CEREMONY } from "@/constants/testIds";
import NarratedText from "@/components/NarratedText";
import { useNarrator } from "@/lib/narrator";

export default function CharacterReveal() {
  const navigate = useNavigate();
  const [reveal, setReveal] = useState(null);
  const [error, setError] = useState("");
  const { speak, muted, supported, currentUtteranceId } = useNarrator();
  const [bodySpoken, setBodySpoken] = useState(false);

  // After the body finishes, softly begin the blessing
  useEffect(() => {
    if (!reveal || !supported || muted) return;
    if (currentUtteranceId === "character-reveal-body") setBodySpoken(true);
    if (bodySpoken && currentUtteranceId === null) {
      const t = setTimeout(
        () => speak(reveal.blessing, "character-reveal-blessing"),
        1200,
      );
      return () => clearTimeout(t);
    }
  }, [currentUtteranceId, bodySpoken, reveal, muted, supported, speak]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.post("/soul/character-reveal");
        if (alive) setReveal(data.reveal);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "The reveal did not come.");
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div
      data-testid={CEREMONY.characterContainer}
      className="relative min-h-screen bg-[#080808] overflow-hidden flex items-center justify-center"
    >
      <div className="starfield-subtle" />
      {/* central sigil */}
      <motion.div
        aria-hidden
        initial={{ opacity: 0, scale: 0.6, rotate: -8 }}
        animate={{ opacity: 0.25, scale: 1, rotate: 0 }}
        transition={{ duration: 3.5, ease: "easeInOut" }}
        className="absolute w-[600px] h-[600px] rounded-full border border-[#C8A96E]/30 drift"
      >
        <div className="absolute inset-8 rounded-full border border-[#C8A96E]/20" />
        <div className="absolute inset-24 rounded-full border border-[#C8A96E]/15" />
      </motion.div>

      <div className="relative z-10 max-w-3xl px-8 text-center">
        <div className="font-overline mb-12 text-[#C8A96E]">Character Reveal</div>

        <AnimatePresence>
          {!reveal && !error && (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.2 }}
              className="font-heading text-3xl text-[#F4EFE6]/70 breathe"
            >
              Listening for your soul name…
            </motion.div>
          )}

          {error && (
            <motion.div
              key="err"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="font-heading text-xl text-[#F4EFE6]/70"
            >
              {error}
            </motion.div>
          )}

          {reveal && (
            <motion.div
              key="reveal"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.6, ease: "easeInOut" }}
            >
              <motion.h1
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.6, delay: 0.3 }}
                className="font-heading text-4xl sm:text-6xl text-[#F4EFE6] font-light italic leading-tight"
              >
                {reveal.archetype_title}
              </motion.h1>

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.6, delay: 1.4 }}
                className="mt-12"
              >
                <NarratedText
                  id="character-reveal-body"
                  text={reveal.body}
                  className="font-heading text-xl sm:text-2xl text-[#F4EFE6]/85 leading-relaxed"
                  delay={1600}
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 2, delay: 2.8 }}
                className="mt-14 pt-10 border-t border-[#C8A96E]/20"
              >
                <div className="font-overline mb-4">A blessing</div>
                <NarratedText
                  id="character-reveal-blessing"
                  text={reveal.blessing}
                  autoSpeak={false}
                  className="font-heading text-2xl sm:text-3xl text-[#C8A96E] italic font-light"
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1.4, delay: 4.4 }}
                className="mt-16"
              >
                <button
                  data-testid={CEREMONY.characterContinueBtn}
                  onClick={() => navigate("/ceremony/deck")}
                  className="btn-sacred"
                >
                  Draw my deck
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
